import { collection, getDocs, collectionGroup, doc, setDoc, updateDoc, deleteDoc, addDoc, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { slugify } from "../utils/slugify";

export const emptyKitchen = {
  description: "",
  descriptionKa: "",
  gallery: [],
  mealTypes: [],
  breakfastPrice: "",
  lunchPrice: "",
  dinnerPrice: "",
  guestKitchenAccess: false,
  preOrderRequired: false,
  serviceHoursFrom: "",
  serviceHoursTo: "",
  diningArea: "",
  diningAreaKa: "",
  dietaryOptions: [],
  paymentMethods: [],
};

export const emptyHotelProfile = {
  name: "",
  nameKa: "",
  slug: "",
  slugKa: "",
  tagline: "",
  taglineKa: "",
  description: "",
  descriptionKa: "",
  location: "",
  locationKa: "",
  propertyType: "",
  propertyTypeKa: "",
  address: "",
  addressKa: "",
  lat: null,
  lng: null,
  distanceCenterKm: "",
  distanceAirportKm: "",
  image: "",
  coverImage: "",
  gallery: [],
  amenities: [],
  rooms: [],
  faq: [],
  nearbyPlaces: [],
  kitchen: emptyKitchen,
  dishes: [],
  featured: false,
  active: true,
  contact: { phone: "", whatsapp: "", email: "", website: "" },
};

export function mergeHotelProfile(hotel) {
  return {
    ...emptyHotelProfile,
    ...hotel,
    contact: { ...emptyHotelProfile.contact, ...(hotel.contact || {}) },
    gallery: hotel.gallery || [],
    amenities: hotel.amenities || [],
    rooms: hotel.rooms || [],
    faq: hotel.faq || [],
    nearbyPlaces: hotel.nearbyPlaces || [],
    kitchen: { ...emptyKitchen, ...(hotel.kitchen || {}) },
    dishes: hotel.dishes || [],
  };
}

export async function fetchHotels() {
  const snap = await getDocs(collection(db, "hotels"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function saveHotel(form, editing) {
  const slug = form.slug || slugify(form.name);
  const slugKa = form.slugKa || (form.nameKa ? slugify(form.nameKa) : slug);
  const data = { ...form, slug, slugKa, updatedAt: new Date() };

  if (editing) {
    // merge: true so fields not present on the form (e.g. hotelUserId, which
    // the owner's own edit form never touches) survive the save.
    await setDoc(doc(db, "hotels", editing.id), data, { merge: true });
    return { ...data, id: editing.id };
  } else {
    const ref = await addDoc(collection(db, "hotels"), {
      ...data,
      createdAt: new Date(),
    });
    return { ...data, id: ref.id };
  }
}

export async function deleteHotel(id) {
  await deleteDoc(doc(db, "hotels", id));
}

export async function setHotelActive(hotelId, active) {
  await setDoc(doc(db, "hotels", hotelId), { active }, { merge: true });
}

export async function setHotelUserId(hotelId, uid) {
  await setDoc(
    doc(db, "hotels", hotelId),
    { hotelUserId: uid || null },
    { merge: true },
  );
}

export async function fetchInquiries(hotelId) {
  const snap = await getDocs(
    query(collection(db, "hotels", hotelId, "inquiries"), orderBy("createdAt", "desc")),
  );
  return snap.docs.map((d) => ({ id: d.id, hotelId, ...d.data() }));
}

export async function fetchAllInquiries() {
  const snap = await getDocs(
    query(collectionGroup(db, "inquiries"), orderBy("createdAt", "desc")),
  );
  return snap.docs.map((d) => ({ id: d.id, hotelId: d.ref.parent.parent.id, ...d.data() }));
}

export async function markInquiryRead(hotelId, inquiryId) {
  await updateDoc(doc(db, "hotels", hotelId, "inquiries", inquiryId), { read: true });
}

export async function deleteInquiry(hotelId, inquiryId) {
  await deleteDoc(doc(db, "hotels", hotelId, "inquiries", inquiryId));
}

export async function fetchReviewsForHotel(hotelId) {
  const snap = await getDocs(
    query(collection(db, "hotels", hotelId, "reviews"), orderBy("createdAt", "desc")),
  );
  return snap.docs.map((d) => ({ id: d.id, hotelId, ...d.data() }));
}

export async function fetchAllReviews() {
  const snap = await getDocs(
    query(collectionGroup(db, "reviews"), orderBy("createdAt", "desc")),
  );
  return snap.docs.map((d) => ({ id: d.id, hotelId: d.ref.parent.parent.id, ...d.data() }));
}

export async function markReviewRead(hotelId, reviewId) {
  await updateDoc(doc(db, "hotels", hotelId, "reviews", reviewId), { read: true });
}

export async function deleteReview(hotelId, reviewId) {
  await deleteDoc(doc(db, "hotels", hotelId, "reviews", reviewId));
}

export async function saveReviewReply(hotelId, reviewId, replyText) {
  await updateDoc(doc(db, "hotels", hotelId, "reviews", reviewId), {
    ownerReply: replyText,
    ownerReplyAt: replyText ? new Date() : null,
  });
}
